import { useMemo } from "react";
import THEME from "../theme";

/**
 * SVG map of the West Coast showing candidate data center sites.
 * Sites are color-coded by risk level and clickable for selection.
 *
 * Coordinate projection: simple linear mapping from lat/lng to SVG viewbox.
 * Lat range: 32°N–55°N, Lng range: -135°W to -115°W
 */
export default function WestCoastMap({ sites, selectedSite, onSelectSite }) {
  const mapPoints = useMemo(
    () =>
      sites.map((s) => ({
        ...s,
        x: ((s.lng + 135) / 20) * 280 + 20,
        y: ((55 - s.lat) / 23) * 500 + 20,
      })),
    [sites]
  );

  const riskColors = {
    CRITICAL: THEME.danger,
    HIGH: THEME.warning,
    MODERATE: THEME.accent,
  };

  return (
    <svg viewBox="0 0 320 540" style={{ width: "100%", maxHeight: 500 }}>
      <defs>
        <linearGradient id="oceanGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0a1628" />
          <stop offset="100%" stopColor="#0d1f3c" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Ocean background */}
      <rect width="320" height="540" fill="url(#oceanGrad)" rx="8" />

      {/* Simplified coastline */}
      <path
        d="M280,20 L270,40 L265,80 L250,120 L235,160 L220,180 L215,200 L225,215 L220,230 L210,250 L215,270 L205,310 L195,340 L200,360 L190,400 L185,420 L195,440 L200,470 L210,500 L220,530"
        fill="none"
        stroke="#1e3a5f"
        strokeWidth="2"
      />
      {/* Land mass */}
      <path
        d="M280,20 L270,40 L265,80 L250,120 L235,160 L220,180 L215,200 L225,215 L220,230 L210,250 L215,270 L205,310 L195,340 L200,360 L190,400 L185,420 L195,440 L200,470 L210,500 L220,530 L320,530 L320,20 Z"
        fill="#111827"
        stroke="none"
        opacity="0.8"
      />

      {/* Latitude grid */}
      {[0, 1, 2, 3, 4].map((i) => (
        <g key={i}>
          <line
            x1="0"
            y1={i * 130 + 20}
            x2="320"
            y2={i * 130 + 20}
            stroke="#1e293b"
            strokeWidth="0.5"
            strokeDasharray="4,8"
          />
          <text
            x="4"
            y={i * 130 + 16}
            fill={THEME.textDim}
            fontSize="8"
            fontFamily="monospace"
          >
            {(55 - i * 5.75).toFixed(1)}°N
          </text>
        </g>
      ))}

      {/* Site markers */}
      {mapPoints.map((site) => {
        const isSelected = selectedSite?.id === site.id;
        const color = riskColors[site.riskLevel] || THEME.accent;
        return (
          <g
            key={site.id}
            onClick={() => onSelectSite(site)}
            style={{ cursor: "pointer" }}
          >
            {/* Selection ring */}
            {isSelected && (
              <circle
                cx={site.x}
                cy={site.y}
                r="20"
                fill={`${color}20`}
                stroke={color}
                strokeWidth="1"
                strokeDasharray="3,3"
              >
                <animate
                  attributeName="r"
                  values="18;22;18"
                  dur="2s"
                  repeatCount="indefinite"
                />
              </circle>
            )}

            {/* Marker dot */}
            <circle
              cx={site.x}
              cy={site.y}
              r={isSelected ? 7 : 5}
              fill={color}
              filter="url(#glow)"
            >
              <animate
                attributeName="opacity"
                values="0.8;1;0.8"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </circle>

            {/* Label */}
            <text
              x={site.x + (site.x > 160 ? -10 : 10)}
              y={site.y - 12}
              fill={isSelected ? "#fff" : THEME.textMuted}
              fontSize="9"
              fontWeight={isSelected ? "700" : "500"}
              fontFamily="monospace"
              textAnchor={site.x > 160 ? "end" : "start"}
            >
              {site.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
